import { default as React } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { Grid } from '@material-ui/core';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { isArgocdAvailable } from '@roadiehq/backstage-plugin-argo-cd';
import {
  isSkyStageAdmin,
  useSkyStageAdminEmails,
  useMicroappEnvironmentStatus,
} from './hooks';
import { EnvironmentAwareArgoCDCard } from './EnvironmentAwareArgoCDCard';

/**
 * This component displays ArgoCD card below the About card for Hosted microapps.
 * - For creators: Shows ArgoCD below (Config is shown beside About)
 * - For non-creators: Returns null (ArgoCD is already shown beside About by MicroappSecondaryCard)
 */
export const MicroappArgoCDCard = () => {
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const adminEmails = useSkyStageAdminEmails();
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');

  // Get current user's email from identity API
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const credentials = await identityApi.getCredentials();

        if (credentials.token) {
          const tokenParts = credentials.token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));

            if (payload.email) {
              setCurrentUserEmail(payload.email);
            } else if (payload.sub) {
              const match = payload.sub.match(/user:(?:default|[^/]+)\/(.+)/);
              if (match && match[1]) {
                const localPart = match[1];
                setCurrentUserEmail(`${localPart}@wellsky.com`);
              }
            }
          }
        }
      } catch (error) {
        // Silently handle errors
      }
    };

    fetchUserEmail();
  }, [identityApi]);

  const appType = entity.metadata.annotations?.['microapp/appType'];
  const isHostedApp = appType === 'Hosted';
  const appCreatedBy =
    entity.metadata.annotations?.['microapp/createdBy'] || '';

  // Check if current user is the creator. SkyStage admins are treated as
  // creators so they get the full owner layout (Config beside About, ArgoCD
  // below) on every microapp.
  const isCreator =
    isSkyStageAdmin(currentUserEmail, adminEmails) ||
    (currentUserEmail &&
      appCreatedBy &&
      currentUserEmail.toLowerCase() === appCreatedBy.toLowerCase());

  // IDP-983: live per-environment status decides whether to show ArgoCD cards.
  // Show staging card whenever staging is deployed (even if production isn't yet).
  const { status } = useMicroappEnvironmentStatus(entity.metadata.name);
  const productionDeployed = status?.production?.deployed ?? false;
  const stagingDeployed = status?.staging?.deployed ?? false;

  // Detect staging-only apps (the catalog entity IS the staging entity)
  // For staging-only apps, the argocd/app-name ends with '-staging'
  const argoAppName = entity.metadata.annotations?.['argocd/app-name'];
  const isStagingEntity = argoAppName?.endsWith('-staging') ?? false;

  // Create production entity:
  // - For staging-only apps: derive by removing '-staging' suffix
  // - For normal apps: use the entity as-is
  const productionEntity = useMemo<Entity | null>(() => {
    if (!argoAppName) return null;
    if (isStagingEntity) {
      // Staging entity: derive production by removing '-staging'
      const prodAppName = argoAppName.replace(/-staging$/, '');
      return {
        ...entity,
        metadata: {
          ...entity.metadata,
          annotations: {
            ...(entity.metadata.annotations ?? {}),
            'argocd/app-name': prodAppName,
          },
        },
      };
    }
    // Normal: entity is already production
    return entity;
  }, [entity, argoAppName, isStagingEntity]);

  // Create staging entity:
  // - For staging-only apps: use entity as-is (already has -staging)
  // - For normal apps: derive by adding '-staging' suffix
  const stagingEntity = useMemo<Entity | null>(() => {
    if (!argoAppName) return null;
    if (isStagingEntity) {
      // Entity is already staging
      return entity;
    }
    // Normal: derive staging from production
    return {
      ...entity,
      metadata: {
        ...entity.metadata,
        annotations: {
          ...(entity.metadata.annotations ?? {}),
          'argocd/app-name': `${argoAppName}-staging`,
        },
      },
    };
  }, [entity, argoAppName, isStagingEntity]);

  // Only show ArgoCD below if:
  // 1. It's a Hosted app
  // 2. ArgoCD is available
  // 3. User is the creator/admin (non-creators see ArgoCD beside About card)
  if (!isHostedApp || !isArgocdAvailable(entity) || !isCreator) {
    return null;
  }

  // IDP-983 FIX: Show ArgoCD cards based on actual deployment status.
  // - If only production is deployed: show production card (full width)
  // - If only staging is deployed: show staging card (full width)
  // - If both are deployed: show both side-by-side
  const showBoth = productionDeployed && stagingDeployed;
  const cardWidth = showBoth ? 6 : 12;

  // IDP-1175: Both staging and production use the same ArgoCD instance.
  // The app name suffix (-staging) distinguishes them.
  // For staging-only entities, we derive production by removing the suffix.
  return (
    <>
      {productionDeployed && productionEntity && (
        <Grid item md={cardWidth} xs={12}>
          <EnvironmentAwareArgoCDCard
            environment="production"
            title="Production"
            entity={productionEntity}
          />
        </Grid>
      )}
      {stagingDeployed && stagingEntity && (
        <Grid item md={cardWidth} xs={12}>
          <EnvironmentAwareArgoCDCard
            environment="staging"
            title="Staging"
            entity={stagingEntity}
          />
        </Grid>
      )}
      {!productionDeployed && !stagingDeployed && (
        <Grid item xs={12}>
          <EnvironmentAwareArgoCDCard
            environment="production"
            title="ArgoCD"
            entity={productionEntity || entity}
          />
        </Grid>
      )}
    </>
  );
};
